import { get, pathAsArray, pathAsString } from './shared';

// Immutable get/set object properties via a path like 'a.b[2].c', or ['a', 'b', 2, 'c'].
//
//    Note that the output object may have references to objects in the input, which is deliberate for Redux.
//    Doesn't check for cyclic references when recursing, if we ever need it to we can do it. OK for Redux state.
//
//    Inpsired by object-path-immutable, but doesn't really borrow the source at all.
//    https://github.com/mariocasciaro/object-path-immutable/blob/master/dist/object-path-immutable.js
//

const SHOULD_REMOVE = Symbol('_immutable_object_path_should_remove_');

/**
 * Delete a property at any depth on a copy of targetObject and return the new object with the deleted property removed.
 * @param {object} target The object to be traversed
 * @param {string} path The path of the property that should be deleted on the object.
 */
export const del = (target: any, path: any) => set(target, path, SHOULD_REMOVE);

/**
 * Make multiple set()'s on a copy of targetObject using another objects key-value pairs (modificationGroup)
 * @param {object} target The target object to have properties set on.
 * @param {string} path The (dot seperated string) path relative to the targetObject to set a value for.
 * @param {object} valuesObject An object where keys are used as partial paths, and values are set on the path 'modificationPath + key'
 * @param {bool} createPath Whether or not missing object path should be created if missing.
 */
export const setMultiple = (target: any, path: any, valuesObject: any, createPath: boolean = false) => {
  if (typeof valuesObject !== 'object') {
    throw new Error(
      `Attempted to setMultiple using a non-object (${JSON.stringify(valuesObject)}) for property updates, ` +
        `expected an object using immutableObjectPath.`
    );
  }

  const pathStr = pathAsString(path);
  Object.keys(valuesObject).forEach(
    (key) => (target = set(target, `${pathStr}.${key}`, valuesObject[key], createPath))
  );

  return target;
};

/**
 * Ensure an object structure exists that fulfills the given path.
 *
 * @param {object} target The target object to search
 * @param {string} path The path to search
 */
export const createPathIfNotExists = (target: any, path: any) => {
  const pathArr = pathAsArray(path) as any;

  // Check this before we start, because we'll be cloning objects all the way down for nothing otherwise.
  if (get(target, pathArr) !== undefined) return target;

  const [head, ...tail] = pathArr;

  // Make sure we have an object to work with
  if (target === undefined) {
    target = isNaN(head) ? {} : [];
  }

  // Recursively ensure full path exists and create it if it does not (except for the final element)
  if (tail.length > 0) {
    let clone;
    if (Array.isArray(target)) {
      clone = [...target];
    } else {
      clone = { ...target };
    }
    clone[head] = createPathIfNotExists(clone[head], tail);
    return clone;
  } else {
    return target;
  }
};

/**
 * Create a copy of a targetObject and set a value at specific path on targetObject
 * @param {object} target The target object to have properties set on.
 * @param {string} path The (dot seperated string) path relative to the targetObject to set a value for.
 * @param {*} value The value to be set at the path on the targetObject.
 * @param {bool} createPath Whether or not missing object path should be created if missing.
 */
export const set = (target: any, path: any, value: any, createPath: boolean = false): any => {
  const pathArr = pathAsArray(path) as any;

  if (createPath) {
    target = createPathIfNotExists(target, pathArr);
  }

  const [head, ...tail] = pathArr;

  // Recurse if we haven't reached the end of the path
  if (tail.length > 0) {
    if (Array.isArray(target)) {
      const clone = [...target];
      clone[head] = set(clone[head], tail, value);
      return clone;
    } else {
      return { ...target, [head]: set(target[head], tail, value) };
    }
  }

  // Update the property once found
  if (value === SHOULD_REMOVE) {
    if (Array.isArray(target)) {
      const index = parseInt(head, 10);
      const clone = [...target.slice(0, index), ...target.slice(index + 1)];
      return clone;
    } else {
      const obj2 = { ...target };
      delete obj2[head];
      return obj2;
    }
  } else {
    if (Array.isArray(target)) {
      const clone = [...target];
      clone[head] = value;
      return clone;
    } else {
      return { ...target, [head]: value };
    }
  }
};
